<template>
  <v-app>
    <v-content>
      <v-container color="grey" fluid fill-height>
        <v-layout align-center justify-center>
          <v-flex xs12 sm8 md4>
            <v-card class="elevation-12">
              <v-toolbar dark color="primary">
                <v-toolbar-title>Weryfikacja dwuetapowa</v-toolbar-title>

                <v-spacer />

                <v-toolbar-title>{{ applicationTitle }}</v-toolbar-title>
              </v-toolbar>

              <v-card-text>
                <p class="mb-3">
                  Na Twój adres e-mail został wysłany kod weryfikacyjny. Wpisz go poniżej, aby dokończyć logowanie.
                </p>

                <v-alert :value="message" :type="messageType" outline>{{ message }}</v-alert>

                <v-text-field
                  v-model="code"
                  label="Kod weryfikacyjny"
                  name="mfa-code"
                  placeholder="Wpisz kod"
                  autocomplete="one-time-code"
                  @keyup.enter="verifyMfaCode"
                />
              </v-card-text>

              <v-card-actions>
                <v-btn text :disabled="loading" @click="cancelMfa">Wróć do logowania</v-btn>

                <v-spacer />

                <v-btn text :disabled="loading || resendSeconds > 0" @click="resendMfaCode">{{ resendLabel }}</v-btn>

                <v-btn color="primary" :loading="loading" @click="verifyMfaCode">Zweryfikuj</v-btn>
              </v-card-actions>
            </v-card>
          </v-flex>
        </v-layout>
      </v-container>
    </v-content>
  </v-app>
</template>

<script>
import { config } from '@/utils/config';
import {
  buildMfaCooldownMessage,
  buildMfaResendLabel,
  clearStoredMfaCooldown,
  getMfaCooldownSeconds,
  getStoredMfaCooldownSeconds,
  MFA_LOGIN_RESEND_STORAGE_KEY,
  storeMfaCooldown
} from '@/utils/mfa';
import { formatError } from '@witkac/kartamiejska-shared';

export default {
  data() {
    return {
      applicationTitle: config.applicationTitle,
      code: '',
      loading: false,
      resendSeconds: 0,
      resendTimerId: null,
      message: '',
      messageType: 'info'
    };
  },
  computed: {
    pendingMfaLogin() {
      return this.$store.state.auth.pendingMfaLogin;
    },
    resendLabel() {
      return buildMfaResendLabel(this.resendSeconds);
    }
  },
  created() {
    const pending = this.ensurePendingLogin();
    if (!pending) {
      return;
    }

    const seconds = this.getRemainingCooldownSeconds();
    if (seconds > 0) {
      this.startResendCountdown(seconds);
      this.setMessage(buildMfaCooldownMessage(), 'info');
      return;
    }

    this.requestMfaCode();
  },
  beforeDestroy() {
    this.clearResendTimer();
  },
  methods: {
    ensurePendingLogin() {
      if (!this.pendingMfaLogin) {
        this.$router.replace({ name: 'login' });
        return null;
      }

      return this.pendingMfaLogin;
    },

    setMessage(message, type = 'info') {
      this.message = message;
      this.messageType = type;
    },

    startResendCountdown(seconds) {
      this.clearResendTimer();
      this.resendSeconds = Math.max(0, seconds);

      if (this.resendSeconds <= 0) {
        this.clearStoredResendCooldown();
        return;
      }

      this.resendTimerId = setInterval(() => {
        if (this.resendSeconds <= 1) {
          this.clearResendTimer();
          this.resendSeconds = 0;
          this.clearStoredResendCooldown();
          return;
        }
        this.resendSeconds -= 1;
      }, 1000);
    },

    getRemainingCooldownSeconds() {
      if (!this.pendingMfaLogin?.login) {
        return 0;
      }

      return getStoredMfaCooldownSeconds(MFA_LOGIN_RESEND_STORAGE_KEY, this.pendingMfaLogin.login);
    },

    applyCooldown(resendAvailableAt) {
      if (resendAvailableAt) {
        this.storeResendCooldown(resendAvailableAt);
      }

      const seconds = getMfaCooldownSeconds(resendAvailableAt);
      this.startResendCountdown(seconds);
      this.setMessage(buildMfaCooldownMessage(seconds), 'success');
    },

    storeResendCooldown(resendAvailableAt) {
      if (!this.pendingMfaLogin?.login || !resendAvailableAt) {
        return;
      }

      storeMfaCooldown(MFA_LOGIN_RESEND_STORAGE_KEY, this.pendingMfaLogin.login, resendAvailableAt);
    },

    clearStoredResendCooldown() {
      if (!this.pendingMfaLogin?.login) {
        return;
      }

      clearStoredMfaCooldown(MFA_LOGIN_RESEND_STORAGE_KEY, this.pendingMfaLogin.login);
    },

    clearResendTimer() {
      if (this.resendTimerId) {
        clearInterval(this.resendTimerId);
        this.resendTimerId = null;
      }
    },

    async requestMfaCode() {
      const pending = this.ensurePendingLogin();
      if (!pending) {
        return;
      }

      this.loading = true;
      this.message = '';

      try {
        const response = await this.$store.dispatch('auth/requestLoginMfa', {
          login: pending.login,
          password: pending.password
        });

        this.applyCooldown(response?.resendAvailableAt);
      } catch (error) {
        this.setMessage(formatError(error), 'error');
      } finally {
        this.loading = false;
      }
    },

    async resendMfaCode() {
      await this.requestMfaCode();
    },

    async verifyMfaCode() {
      if (!this.code) {
        this.setMessage('Wprowadź kod weryfikacyjny.', 'error');
        return;
      }

      const pending = this.ensurePendingLogin();
      if (!pending) {
        return;
      }

      this.loading = true;
      this.message = '';

      try {
        const loginResult = await this.$store.dispatch('auth/verifyLoginMfa', {
          login: pending.login,
          password: pending.password,
          code: this.code
        });

        this.clearStoredResendCooldown();

        const profileTarget = loginResult.profile.partner ? 'placeSelect' : 'profile';
        const target = loginResult.hasAllConsents ? profileTarget : 'termsCurrent';
        this.$router.push({ name: target });
      } catch (error) {
        this.setMessage(formatError(error), 'error');
      } finally {
        this.loading = false;
      }
    },

    cancelMfa() {
      this.clearResendTimer();
      this.$store.dispatch('auth/setPendingMfaLogin', null);
      this.$router.push({ name: 'login' });
    }
  }
};
</script>
